import {
  Button as HeadlessButton,
  type ButtonProps as HeadlessButtonProps,
} from '@headlessui/react';
import clsx from 'clsx';
import React from 'react';
import { Link } from './link';

const styles = {
  base: [
    // Base
    'relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg border text-base/6 font-semibold',

    // Sizing
    'px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)] sm:text-sm/6',

    // Focus
    'focus:outline-hidden data-focus:outline data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500',

    // Disabled
    'data-disabled:opacity-50',

    // Icon
    '*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:my-0.5 *:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:text-(--btn-icon) sm:*:data-[slot=icon]:my-1 sm:*:data-[slot=icon]:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-hover:[--btn-icon:ButtonText]',
  ],
  solid: [
    // Optical border, implemented as the button background to avoid corner artifacts
    'border-transparent bg-(--btn-border)',

    // Dark mode: border is rendered on `after` so background is set to button background
    'dark:bg-(--btn-bg)',

    // Button background, implemented as foreground layer to stack on top of pseudo-border layer
    'before:absolute before:inset-0 before:-z-10 before:rounded-[calc(var(--radius-lg)-1px)] before:bg-(--btn-bg)',

    // Drop shadow, applied to the inset `before` layer so it blends with the border
    'before:shadow-sm',

    // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
    'dark:before:hidden',

    // Dark mode: Subtle white outline is applied using a border
    'dark:border-white/5',

    // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
    'after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--radius-lg)-1px)]',

    // Inner highlight shadow
    'after:shadow-[shadow:inset_0_1px_rgb(255_255_255/0.15)]',

    // White overlay on hover
    'data-active:after:bg-(--btn-hover-overlay) data-hover:after:bg-(--btn-hover-overlay)',

    // Dark mode: `after` layer expands to cover entire button
    'dark:after:-inset-px dark:after:rounded-lg',

    // Disabled
    'data-disabled:before:shadow-none data-disabled:after:shadow-none',
  ],
  outline: [
    // Base
    'border-zinc-950/10 text-zinc-950 data-active:bg-zinc-950/[2.5%] data-hover:bg-zinc-950/[2.5%]',

    // Dark mode
    'dark:border-white/15 dark:text-white dark:[--btn-bg:transparent] dark:data-active:bg-white/5 dark:data-hover:bg-white/5',

    // Icon
    '[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]',
  ],
  plain: [
    // Base
    'border-transparent text-zinc-950 data-active:bg-zinc-950/5 data-hover:bg-zinc-950/5',

    // Dark mode
    'dark:text-white dark:data-active:bg-white/10 dark:data-hover:bg-white/10',

    // Icon
    '[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]',
  ],
  dropdown: [
    // Base
    'border-transparent text-black data-active:bg-black/5 data-hover:bg-black/5',

    // Dark mode
    'dark:text-white dark:data-active:bg-white/10 dark:data-hover:bg-white/10',

    // Icon
    '[--btn-icon:var(--color-black)] data-active:[--btn-icon:var(--color-black)] data-hover:[--btn-icon:var(--color-black)] dark:[--btn-icon:var(--color-black)] dark:data-active:[--btn-icon:var(--color-black)] dark:data-hover:[--btn-icon:var(--color-black)]',
  ],
  outlineColors: {
    primary: [
      'border-cPrimary text-cPrimary data-active:bg-cPrimary/10 data-hover:bg-cPrimary/10',
      'dark:border-cPrimary dark:text-cPrimary dark:data-active:bg-cPrimary/20 dark:data-hover:bg-cPrimary/20',
      '[--btn-icon:var(--color-cPrimary)] data-active:[--btn-icon:var(--color-cPrimary)] data-hover:[--btn-icon:var(--color-cPrimary)]',
    ],
    red: [
      'border-red-600 text-red-600 data-active:bg-red-50 data-hover:bg-red-50',
      'dark:border-red-500 dark:text-red-500 dark:data-active:bg-red-950/30 dark:data-hover:bg-red-950/30',
      '[--btn-icon:var(--color-red-600)] data-active:[--btn-icon:var(--color-red-700)] data-hover:[--btn-icon:var(--color-red-700)] dark:[--btn-icon:var(--color-red-500)]',
    ],
    orange: [
      'border-orange-600 text-orange-600 data-active:bg-orange-50 data-hover:bg-orange-50',
      'dark:border-orange-500 dark:text-orange-500 dark:data-active:bg-orange-950/30 dark:data-hover:bg-orange-950/30',
      '[--btn-icon:var(--color-orange-600)] data-active:[--btn-icon:var(--color-orange-700)] data-hover:[--btn-icon:var(--color-orange-700)] dark:[--btn-icon:var(--color-orange-500)]',
    ],
    green: [
      'border-green-600 text-green-600 data-active:bg-green-50 data-hover:bg-green-50',
      'dark:border-green-500 dark:text-green-500 dark:data-active:bg-green-950/30 dark:data-hover:bg-green-950/30',
      '[--btn-icon:var(--color-green-600)] data-active:[--btn-icon:var(--color-green-700)] data-hover:[--btn-icon:var(--color-green-700)] dark:[--btn-icon:var(--color-green-500)]',
    ],
    blue: [
      'border-blue-600 text-blue-600 data-active:bg-blue-50 data-hover:bg-blue-50',
      'dark:border-blue-500 dark:text-blue-500 dark:data-active:bg-blue-950/30 dark:data-hover:bg-blue-950/30',
      '[--btn-icon:var(--color-blue-600)] data-active:[--btn-icon:var(--color-blue-700)] data-hover:[--btn-icon:var(--color-blue-700)] dark:[--btn-icon:var(--color-blue-500)]',
    ],
    indigo: [
      'border-indigo-600 text-indigo-600 data-active:bg-indigo-50 data-hover:bg-indigo-50',
      'dark:border-indigo-500 dark:text-indigo-500 dark:data-active:bg-indigo-950/30 dark:data-hover:bg-indigo-950/30',
      '[--btn-icon:var(--color-indigo-600)] data-active:[--btn-icon:var(--color-indigo-700)] data-hover:[--btn-icon:var(--color-indigo-700)] dark:[--btn-icon:var(--color-indigo-500)]',
    ],
    purple: [
      'border-purple-600 text-purple-600 data-active:bg-purple-50 data-hover:bg-purple-50',
      'dark:border-purple-500 dark:text-purple-500 dark:data-active:bg-purple-950/30 dark:data-hover:bg-purple-950/30',
      '[--btn-icon:var(--color-purple-600)] data-active:[--btn-icon:var(--color-purple-700)] data-hover:[--btn-icon:var(--color-purple-700)] dark:[--btn-icon:var(--color-purple-500)]',
    ],
    pink: [
      'border-pink-600 text-pink-600 data-active:bg-pink-50 data-hover:bg-pink-50',
      'dark:border-pink-500 dark:text-pink-500 dark:data-active:bg-pink-950/30 dark:data-hover:bg-pink-950/30',
      '[--btn-icon:var(--color-pink-600)] data-active:[--btn-icon:var(--color-pink-700)] data-hover:[--btn-icon:var(--color-pink-700)] dark:[--btn-icon:var(--color-pink-500)]',
    ],
    zinc: [
      'border-zinc-600 text-zinc-600 data-active:bg-zinc-50 data-hover:bg-zinc-50',
      'dark:border-zinc-400 dark:text-zinc-400 dark:data-active:bg-zinc-800 dark:data-hover:bg-zinc-800',
      '[--btn-icon:var(--color-zinc-600)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:[--btn-icon:var(--color-zinc-400)]',
    ],
    'dark/zinc': [
      'border-zinc-900 text-zinc-900 data-active:bg-zinc-100 data-hover:bg-zinc-100',
      'dark:border-zinc-600 dark:text-zinc-400 dark:data-active:bg-zinc-800 dark:data-hover:bg-zinc-800',
      '[--btn-icon:var(--color-zinc-700)] data-active:[--btn-icon:var(--color-zinc-800)] data-hover:[--btn-icon:var(--color-zinc-800)] dark:[--btn-icon:var(--color-zinc-500)]',
    ],
    light: [
      'border-zinc-300 text-zinc-900 data-active:bg-zinc-100 data-hover:bg-zinc-100',
      'dark:border-zinc-700 dark:text-zinc-300 dark:data-active:bg-zinc-800 dark:data-hover:bg-zinc-800',
      '[--btn-icon:var(--color-zinc-600)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:[--btn-icon:var(--color-zinc-400)]',
    ],
    'dark/white': [
      'border-zinc-900 text-zinc-900 data-active:bg-zinc-100 data-hover:bg-zinc-100',
      'dark:border-zinc-200 dark:text-zinc-200 dark:data-active:bg-zinc-800 dark:data-hover:bg-zinc-800',
      '[--btn-icon:var(--color-zinc-700)] data-active:[--btn-icon:var(--color-zinc-800)] data-hover:[--btn-icon:var(--color-zinc-800)] dark:[--btn-icon:var(--color-zinc-400)]',
    ],
    dark: [
      'border-zinc-900 text-zinc-900 data-active:bg-zinc-100 data-hover:bg-zinc-100',
      'dark:border-zinc-700 dark:text-zinc-300 dark:data-active:bg-zinc-800 dark:data-hover:bg-zinc-800',
      '[--btn-icon:var(--color-zinc-700)] data-active:[--btn-icon:var(--color-zinc-800)] data-hover:[--btn-icon:var(--color-zinc-800)] dark:[--btn-icon:var(--color-zinc-500)]',
    ],
    white: [
      'border-zinc-300 text-zinc-900 data-active:bg-zinc-50 data-hover:bg-zinc-50',
      'dark:border-zinc-600 dark:text-zinc-300 dark:data-active:bg-zinc-800 dark:data-hover:bg-zinc-800',
      '[--btn-icon:var(--color-zinc-600)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:[--btn-icon:var(--color-zinc-400)]',
    ],
    cyan: [
      'border-cyan-600 text-cyan-700 data-active:bg-cyan-50 data-hover:bg-cyan-50',
      'dark:border-cyan-500 dark:text-cyan-400 dark:data-active:bg-cyan-950/30 dark:data-hover:bg-cyan-950/30',
      '[--btn-icon:var(--color-cyan-600)] data-active:[--btn-icon:var(--color-cyan-700)] data-hover:[--btn-icon:var(--color-cyan-700)] dark:[--btn-icon:var(--color-cyan-400)]',
    ],
    amber: [
      'border-amber-600 text-amber-700 data-active:bg-amber-50 data-hover:bg-amber-50',
      'dark:border-amber-500 dark:text-amber-400 dark:data-active:bg-amber-950/30 dark:data-hover:bg-amber-950/30',
      '[--btn-icon:var(--color-amber-600)] data-active:[--btn-icon:var(--color-amber-700)] data-hover:[--btn-icon:var(--color-amber-700)] dark:[--btn-icon:var(--color-amber-400)]',
    ],
    yellow: [
      'border-yellow-600 text-yellow-700 data-active:bg-yellow-50 data-hover:bg-yellow-50',
      'dark:border-yellow-500 dark:text-yellow-400 dark:data-active:bg-yellow-950/30 dark:data-hover:bg-yellow-950/30',
      '[--btn-icon:var(--color-yellow-600)] data-active:[--btn-icon:var(--color-yellow-700)] data-hover:[--btn-icon:var(--color-yellow-700)] dark:[--btn-icon:var(--color-yellow-400)]',
    ],
    lime: [
      'border-lime-600 text-lime-700 data-active:bg-lime-50 data-hover:bg-lime-50',
      'dark:border-lime-500 dark:text-lime-400 dark:data-active:bg-lime-950/30 dark:data-hover:bg-lime-950/30',
      '[--btn-icon:var(--color-lime-600)] data-active:[--btn-icon:var(--color-lime-700)] data-hover:[--btn-icon:var(--color-lime-700)] dark:[--btn-icon:var(--color-lime-400)]',
    ],
    emerald: [
      'border-emerald-600 text-emerald-600 data-active:bg-emerald-50 data-hover:bg-emerald-50',
      'dark:border-emerald-500 dark:text-emerald-500 dark:data-active:bg-emerald-950/30 dark:data-hover:bg-emerald-950/30',
      '[--btn-icon:var(--color-emerald-600)] data-active:[--btn-icon:var(--color-emerald-700)] data-hover:[--btn-icon:var(--color-emerald-700)] dark:[--btn-icon:var(--color-emerald-500)]',
    ],
    teal: [
      'border-teal-600 text-teal-600 data-active:bg-teal-50 data-hover:bg-teal-50',
      'dark:border-teal-500 dark:text-teal-500 dark:data-active:bg-teal-950/30 dark:data-hover:bg-teal-950/30',
      '[--btn-icon:var(--color-teal-600)] data-active:[--btn-icon:var(--color-teal-700)] data-hover:[--btn-icon:var(--color-teal-700)] dark:[--btn-icon:var(--color-teal-500)]',
    ],
    sky: [
      'border-sky-600 text-sky-600 data-active:bg-sky-50 data-hover:bg-sky-50',
      'dark:border-sky-500 dark:text-sky-500 dark:data-active:bg-sky-950/30 dark:data-hover:bg-sky-950/30',
      '[--btn-icon:var(--color-sky-600)] data-active:[--btn-icon:var(--color-sky-700)] data-hover:[--btn-icon:var(--color-sky-700)] dark:[--btn-icon:var(--color-sky-500)]',
    ],
    violet: [
      'border-violet-600 text-violet-600 data-active:bg-violet-50 data-hover:bg-violet-50',
      'dark:border-violet-500 dark:text-violet-500 dark:data-active:bg-violet-950/30 dark:data-hover:bg-violet-950/30',
      '[--btn-icon:var(--color-violet-600)] data-active:[--btn-icon:var(--color-violet-700)] data-hover:[--btn-icon:var(--color-violet-700)] dark:[--btn-icon:var(--color-violet-500)]',
    ],
    fuchsia: [
      'border-fuchsia-600 text-fuchsia-600 data-active:bg-fuchsia-50 data-hover:bg-fuchsia-50',
      'dark:border-fuchsia-500 dark:text-fuchsia-500 dark:data-active:bg-fuchsia-950/30 dark:data-hover:bg-fuchsia-950/30',
      '[--btn-icon:var(--color-fuchsia-600)] data-active:[--btn-icon:var(--color-fuchsia-700)] data-hover:[--btn-icon:var(--color-fuchsia-700)] dark:[--btn-icon:var(--color-fuchsia-500)]',
    ],
    rose: [
      'border-rose-600 text-rose-600 data-active:bg-rose-50 data-hover:bg-rose-50',
      'dark:border-rose-500 dark:text-rose-500 dark:data-active:bg-rose-950/30 dark:data-hover:bg-rose-950/30',
      '[--btn-icon:var(--color-rose-600)] data-active:[--btn-icon:var(--color-rose-700)] data-hover:[--btn-icon:var(--color-rose-700)] dark:[--btn-icon:var(--color-rose-500)]',
    ],
  },
  colors: {
    primary: [
      // 'text-white [--btn-bg:var(--color-cPrimary-400)] [--btn-border:var(--color-cPrimary-500)]/90 [--btn-hover-overlay:var(--color-white)]/10',
      // 'dark:text-white dark:[--btn-bg:var(--color-zinc-600)] dark:[--btn-hover-overlay:var(--color-white)]/5',
      // '[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80',
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-cPrimary)] [--btn-border:var(--color-cPrimary-400)]/90',
      '[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80',
    ],
    'dark/zinc': [
      'text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10',
      'dark:text-white dark:[--btn-bg:var(--color-zinc-600)] dark:[--btn-hover-overlay:var(--color-white)]/5',
      '[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]',
    ],
    light: [
      'text-zinc-950 [--btn-bg:white] [--btn-border:var(--color-zinc-950)]/10 [--btn-hover-overlay:var(--color-zinc-950)]/[2.5%] data-active:[--btn-border:var(--color-zinc-950)]/15 data-hover:[--btn-border:var(--color-zinc-950)]/15',
      'dark:text-white dark:[--btn-hover-overlay:var(--color-white)]/5 dark:[--btn-bg:var(--color-zinc-800)]',
      '[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]',
    ],
    'dark/white': [
      'text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10',
      'dark:text-zinc-950 dark:[--btn-bg:white] dark:[--btn-hover-overlay:var(--color-zinc-950)]/5',
      '[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]',
    ],
    dark: [
      'text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10',
      'dark:[--btn-hover-overlay:var(--color-white)]/5 dark:[--btn-bg:var(--color-zinc-800)]',
      '[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]',
    ],
    white: [
      'text-zinc-950 [--btn-bg:white] [--btn-border:var(--color-zinc-950)]/10 [--btn-hover-overlay:var(--color-zinc-950)]/[2.5%] data-active:[--btn-border:var(--color-zinc-950)]/15 data-hover:[--btn-border:var(--color-zinc-950)]/15',
      'dark:[--btn-hover-overlay:var(--color-zinc-950)]/5',
      '[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-500)] data-hover:[--btn-icon:var(--color-zinc-500)]',
    ],
    zinc: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-zinc-600)] [--btn-border:var(--color-zinc-700)]/90',
      'dark:[--btn-hover-overlay:var(--color-white)]/5',
      '[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]',
    ],
    indigo: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-indigo-500)] [--btn-border:var(--color-indigo-600)]/90',
      '[--btn-icon:var(--color-indigo-300)] data-active:[--btn-icon:var(--color-indigo-200)] data-hover:[--btn-icon:var(--color-indigo-200)]',
    ],
    cyan: [
      'text-cyan-950 [--btn-bg:var(--color-cyan-300)] [--btn-border:var(--color-cyan-400)]/80 [--btn-hover-overlay:var(--color-white)]/25',
      '[--btn-icon:var(--color-cyan-500)]',
    ],
    red: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-red-600)] [--btn-border:var(--color-red-700)]/90',
      '[--btn-icon:var(--color-red-300)] data-active:[--btn-icon:var(--color-red-200)] data-hover:[--btn-icon:var(--color-red-200)]',
    ],
    orange: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-orange-500)] [--btn-border:var(--color-orange-600)]/90',
      '[--btn-icon:var(--color-orange-300)] data-active:[--btn-icon:var(--color-orange-200)] data-hover:[--btn-icon:var(--color-orange-200)]',
    ],
    amber: [
      'text-amber-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-amber-400)] [--btn-border:var(--color-amber-500)]/80',
      '[--btn-icon:var(--color-amber-600)]',
    ],
    yellow: [
      'text-yellow-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-yellow-300)] [--btn-border:var(--color-yellow-400)]/80',
      '[--btn-icon:var(--color-yellow-600)] data-active:[--btn-icon:var(--color-yellow-700)] data-hover:[--btn-icon:var(--color-yellow-700)]',
    ],
    lime: [
      'text-lime-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-lime-300)] [--btn-border:var(--color-lime-400)]/80',
      '[--btn-icon:var(--color-lime-600)] data-active:[--btn-icon:var(--color-lime-700)] data-hover:[--btn-icon:var(--color-lime-700)]',
    ],
    green: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-green-600)] [--btn-border:var(--color-green-700)]/90',
      '[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80',
    ],
    emerald: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-emerald-600)] [--btn-border:var(--color-emerald-700)]/90',
      '[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80',
    ],
    teal: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-teal-600)] [--btn-border:var(--color-teal-700)]/90',
      '[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80',
    ],
    sky: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-sky-500)] [--btn-border:var(--color-sky-600)]/80',
      '[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80',
    ],
    blue: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-blue-600)] [--btn-border:var(--color-blue-700)]/90',
      '[--btn-icon:var(--color-blue-400)] data-active:[--btn-icon:var(--color-blue-300)] data-hover:[--btn-icon:var(--color-blue-300)]',
    ],
    violet: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-violet-500)] [--btn-border:var(--color-violet-600)]/90',
      '[--btn-icon:var(--color-violet-300)] data-active:[--btn-icon:var(--color-violet-200)] data-hover:[--btn-icon:var(--color-violet-200)]',
    ],
    purple: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-purple-500)] [--btn-border:var(--color-purple-600)]/90',
      '[--btn-icon:var(--color-purple-300)] data-active:[--btn-icon:var(--color-purple-200)] data-hover:[--btn-icon:var(--color-purple-200)]',
    ],
    fuchsia: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-fuchsia-500)] [--btn-border:var(--color-fuchsia-600)]/90',
      '[--btn-icon:var(--color-fuchsia-300)] data-active:[--btn-icon:var(--color-fuchsia-200)] data-hover:[--btn-icon:var(--color-fuchsia-200)]',
    ],
    pink: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-pink-500)] [--btn-border:var(--color-pink-600)]/90',
      '[--btn-icon:var(--color-pink-300)] data-active:[--btn-icon:var(--color-pink-200)] data-hover:[--btn-icon:var(--color-pink-200)]',
    ],
    rose: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-rose-500)] [--btn-border:var(--color-rose-600)]/90',
      '[--btn-icon:var(--color-rose-300)] data-active:[--btn-icon:var(--color-rose-200)] data-hover:[--btn-icon:var(--color-rose-200)]',
    ],
  },
};

export type ButtonProps = (
  | { color?: keyof typeof styles.colors; outline?: never; plain?: never; dropdown?: never }
  | { color?: keyof typeof styles.outlineColors; outline: true; plain?: never; dropdown?: never }
  | { color?: never; outline?: never; plain: true; dropdown?: never }
  | { color?: never; outline?: never; plain?: never; dropdown: true }
) & { children: React.ReactNode } & (
    | HeadlessButtonProps
    | React.ComponentPropsWithoutRef<typeof Link>
  );

export const Button = React.forwardRef(function Button(
  { color, outline, plain, dropdown, className, children, ...props }: ButtonProps,
  ref: React.ForwardedRef<HTMLElement>,
) {
  // Type guard to check if props has disabled
  const hasDisabled = (p: unknown): p is { disabled?: boolean } =>
    typeof p === 'object' && p !== null && 'disabled' in p;

  const isDisabled = hasDisabled(props) && Boolean((props as { disabled?: boolean }).disabled);
  const restProps = hasDisabled(props)
    ? Object.fromEntries(Object.entries(props).filter(([k]) => k !== 'disabled'))
    : props;

  const classes = clsx(
    className,
    styles.base,
    outline
      ? color
        ? styles.outlineColors[color]
        : styles.outline
      : plain
        ? styles.plain
        : dropdown
          ? styles.dropdown
          : clsx(styles.solid, styles.colors[color ?? 'primary']),
    isDisabled && 'pointer-events-none', // Add pointer-events-none if disabled
  );

  // Type guard to check if props has href and it's defined
  const hasHref = (p: unknown): p is { href: string } =>
    typeof p === 'object' &&
    p !== null &&
    'href' in p &&
    typeof (p as { href?: unknown }).href === 'string' &&
    Boolean((p as { href?: unknown }).href);

  if (hasHref(props)) {
    return (
      <Link
        {...restProps}
        href={props.href}
        className={classes}
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        data-disabled={isDisabled ? true : undefined}
        aria-disabled={isDisabled ? 'true' : undefined}
        tabIndex={isDisabled ? -1 : undefined}
      >
        <TouchTarget>{children}</TouchTarget>
      </Link>
    );
  }

  return (
    <HeadlessButton {...props} className={clsx(classes, 'cursor-default')} ref={ref}>
      <TouchTarget>{children}</TouchTarget>
    </HeadlessButton>
  );
});

/* Expand the hit area to at least 44×44px on touch devices */
export function TouchTarget({ children }: { children: React.ReactNode }) {
  return (
    <>
      {children}
      <span
        className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 pointer-fine:hidden"
        aria-hidden="true"
      />
    </>
  );
}
